import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {Loader2, Search} from 'lucide-react';
import clsx from 'clsx';
import AdminService from '@/modules/admin/services/adminService';
import {UNMAPPED_QUERY_KEY} from '@/modules/admin/components/BiomarkerDisplayRulesPanel';
import type {UnmappedAnalyte} from '@/shared/types/api';

type SourceFilter = 'all' | 'ohd' | 'elis';

const SOURCE_LABEL: Record<UnmappedAnalyte['source'], string> = {ohd: 'OHD', elis: 'ELIS'};

/** Show this many organization names before collapsing to "+N more". */
const MAX_ORGS = 3;

const matches = (row: UnmappedAnalyte, term: string): boolean => {
    if (!term) return true;
    const t = term.toLowerCase();
    return [
        row.rawIdentifier,
        row.exampleName,
        row.loinc,
        row.suggestion?.display_name,
        ...row.organizations.map((o) => o.name),
    ]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(t));
};

/**
 * Lab results we receive but cannot show properly: they reached no display
 * rule, so a patient sees them under "Other" with a raw or catalog name and,
 * usually, no range. Fed by the unmapped-analyte alert, which used to be
 * visible only as an ops email.
 *
 * "Map it" hands a pre-filled draft to the Display rules editor (`onMap`).
 * Nothing is saved from here — the admin reviews the draft and saves it
 * there, and the row then drops off this list.
 */
const UnmappedAnalytesPanel: React.FC<{onMap?: (row: UnmappedAnalyte) => void}> = ({onMap}) => {
    const [search, setSearch] = React.useState('');
    const [source, setSource] = React.useState<SourceFilter>('all');

    const listQuery = useQuery({
        queryKey: UNMAPPED_QUERY_KEY,
        queryFn: () => AdminService.listUnmappedAnalytes(),
    });

    const all = listQuery.data?.rows ?? [];
    const term = search.trim();
    const rows = all.filter((r) => (source === 'all' || r.source === source) && matches(r, term));
    const mappable = all.filter((r) => r.suggestion).length;

    return (
        <div className="space-y-6">
            <div className="rounded-lg border border-mld-panel-border bg-mld-menu-background p-6 shadow-sm">
                <div className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-mld-panel-border pb-4">
                    <div>
                        <h2 className="text-xl font-semibold text-mld-text-primary">Needs mapping</h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Lab results we receive but have no display rule for. Patients see these under{' '}
                            <span className="font-medium">Other</span>, with a raw or catalog name and usually no range.
                            Most patients affected first. <span className="font-medium">Map it</span> opens the Display
                            rules editor pre-filled; review it and save, and the row leaves this list.
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <label className="relative block w-72">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <input
                            aria-label="Search unmapped analytes"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search code, name, LOINC or organization"
                            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary"
                        />
                    </label>
                    <div className="flex items-center gap-1" role="group" aria-label="Filter by source">
                        {(['all', 'ohd', 'elis'] as const).map((key) => (
                            <button
                                key={key}
                                type="button"
                                aria-pressed={source === key}
                                onClick={() => setSource(key)}
                                className={clsx(
                                    'rounded-md border px-3 py-1.5 text-sm',
                                    source === key
                                        ? 'border-mld-primary bg-mld-primary/10 font-medium text-mld-primary'
                                        : 'border-mld-panel-border text-gray-600 hover:text-mld-menu-text'
                                )}>
                                {key === 'all' ? 'All' : SOURCE_LABEL[key]}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-4 overflow-hidden rounded-lg border border-mld-panel-border">
                    <div className="max-h-[60vh] overflow-auto">
                        <table className="min-w-full divide-y divide-mld-panel-border text-sm">
                            <thead className="sticky top-0 bg-mld-menu-background text-left text-[11px] uppercase tracking-[0.12em] text-gray-500">
                                <tr>
                                    <th className="px-4 py-2">Analyte</th>
                                    <th className="px-4 py-2">LOINC</th>
                                    <th className="w-32 px-4 py-2">Patients</th>
                                    <th className="px-4 py-2">Organizations</th>
                                    <th className="px-4 py-2">Suggested name</th>
                                    <th className="w-32 px-4 py-2 text-right" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-mld-panel-border bg-white">
                                {listQuery.isLoading ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                                            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                                            <div className="mt-2 text-xs">
                                                Counting results across every organization&hellip;
                                            </div>
                                        </td>
                                    </tr>
                                ) : listQuery.isError ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-10 text-center text-red-600">
                                            Unable to load unmapped analytes.
                                        </td>
                                    </tr>
                                ) : rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                                            {all.length === 0
                                                ? 'Every analyte we have received is mapped.'
                                                : 'No analytes match.'}
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row) => (
                                        <tr
                                            key={`${row.source}|${row.rawIdentifier}|${row.loinc ?? ''}`}
                                            className="align-top">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-xs text-mld-text-primary">
                                                        {row.rawIdentifier}
                                                    </span>
                                                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                                                        {SOURCE_LABEL[row.source]}
                                                    </span>
                                                </div>
                                                {row.exampleName && row.exampleName !== row.rawIdentifier ? (
                                                    <div className="mt-0.5 text-xs text-gray-500">
                                                        {row.exampleName}
                                                    </div>
                                                ) : null}
                                            </td>

                                            <td className="px-4 py-3 font-mono text-xs text-mld-text-primary">
                                                {row.loinc ?? (
                                                    <span className="font-sans text-gray-400">none sent</span>
                                                )}
                                            </td>

                                            <td className="px-4 py-3 text-gray-500">
                                                <div className="text-mld-text-primary">
                                                    {row.patientCount.toLocaleString()}
                                                </div>
                                                <div className="text-xs">
                                                    {row.resultCount.toLocaleString()} result
                                                    {row.resultCount === 1 ? '' : 's'}
                                                </div>
                                            </td>

                                            <td className="px-4 py-3 text-gray-600">
                                                {row.organizations.length === 0 ? (
                                                    <span className="text-gray-400">&mdash;</span>
                                                ) : (
                                                    <span title={row.organizations.map((o) => o.name).join(', ')}>
                                                        {row.organizations
                                                            .slice(0, MAX_ORGS)
                                                            .map((o) => o.name)
                                                            .join(', ')}
                                                        {row.organizations.length > MAX_ORGS
                                                            ? ` +${row.organizations.length - MAX_ORGS} more`
                                                            : ''}
                                                    </span>
                                                )}
                                            </td>

                                            <td className="px-4 py-3">
                                                {row.suggestion ? (
                                                    <>
                                                        <div className="text-mld-text-primary">
                                                            {row.suggestion.display_name}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            from {row.suggestion.nameSource}
                                                            {row.suggestion.display_mode === 'banded'
                                                                ? ' · bands from lab range'
                                                                : ''}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-gray-400">&mdash;</span>
                                                )}
                                            </td>

                                            <td className="px-4 py-3 text-right">
                                                {row.suggestion ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => onMap?.(row)}
                                                        className="text-sm font-medium text-mld-primary hover:underline">
                                                        Map it &rarr;
                                                    </button>
                                                ) : (
                                                    <span
                                                        className="whitespace-nowrap text-xs text-gray-500"
                                                        title="The lab sent no LOINC code, so a display rule can't be keyed to it yet. The lab needs to send a code, or an engineer adds a code alias.">
                                                        Needs a LOINC first
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-3 text-sm text-gray-500">
                    {all.length.toLocaleString()} unmapped &middot; {mappable.toLocaleString()} can be mapped here
                    {rows.length !== all.length ? ` · showing ${rows.length.toLocaleString()}` : ''}
                </div>
            </div>
        </div>
    );
};

export default UnmappedAnalytesPanel;
